// Grupo «Crédito» de las promos de un equipo: enganche, tasa, monto y bono a tarjeta de loan-offerings.
// Solo lectura; lo usan el detalle del catálogo y «Accesorios».
import { Percent } from 'lucide-react';
import { detailReachLabel, levelOnlyLabel, type CreditPromotionRow } from '../creditPromotions';
import { effectChips } from '../promotionDetail';
import type { PlanLevel } from '../types';
import { LevelChip } from './LevelChip';
import { PromotionGroup, PromotionGroupRow, SelectablePromotionRow } from './PromotionGroup';

/** Aclaración bajo el título: sin nivel, se aplica al cotizar, o el cliente no califica. */
export type CreditGroupNote = 'levelPending' | 'atQuote' | 'notEligible';

const NOTE_TEXT: Record<Exclude<CreditGroupNote, 'notEligible'>, string> = {
    levelPending: 'Según su nivel',
    atQuote: 'Se aplica al cotizar con SureKeep',
};

/** Filas ya filtradas por nivel; con `notEligible` solo dice que no califica. */
export function CreditPromotionsGroup({
    rows,
    clientLevel,
    note,
    applied = false,
    appliedLevel,
    hideLabel = false,
}: {
    rows: readonly CreditPromotionRow[];
    clientLevel?: PlanLevel | null;
    note?: CreditGroupNote | undefined;
    /** «Accesorios»: cada promo con su casilla marcada (si ya hay nivel) y sin editar. */
    applied?: boolean;
    /** Nivel del cliente para marcarlas; sin él ninguna se da por segura. */
    appliedLevel?: PlanLevel | null | undefined;
    /** Ocultar el header CRÉDITO: el color morado basta para identificar el grupo. */
    hideLabel?: boolean;
}) {
    if (note === 'notEligible') {
        return (
            <PromotionGroup
                label="Crédito"
                icon={Percent}
                tone="credit"
                hideLabel={hideLabel}
                items={
                    rows.length === 0
                        ? []
                        : [
                              {
                                  key: 'not-eligible',
                                  node: (
                                      <p className="text-muted-foreground">
                                          No disponible: el cliente no califica a crédito.
                                      </p>
                                  ),
                              },
                          ]
                }
            />
        );
    }
    return (
        <PromotionGroup
            label={applied ? 'Promos de crédito' : 'Crédito'}
            icon={Percent}
            tone="credit"
            hideLabel={hideLabel}
            {...(note !== undefined && { note: NOTE_TEXT[note] })}
            items={rows.map((row) => {
                const levelLabel = levelOnlyLabel(row.targetLevels);
                const content = (
                    <PromotionGroupRow
                        icon={Percent}
                        tone="credit"
                        name={row.name}
                        aside={
                            row.targetLevels.length > 0 && (
                                <span className="flex flex-none flex-wrap justify-end gap-1">
                                    {row.targetLevels.map((level) => (
                                        <LevelChip key={level} level={level} size="sm" />
                                    ))}
                                </span>
                            )
                        }
                        description={[
                            ...effectChips(row.effect).map((c) => c.label),
                            detailReachLabel(row.reach),
                        ].join(' · ')}
                        meta={
                            row.targetLevels.length > 0 && clientLevel === undefined
                                ? levelLabel
                                : undefined
                        }
                    />
                );
                return {
                    key: row.promotionId,
                    // En «Accesorios» se aplican solas al cotizar: marcadas y sin editar.
                    node: applied ? (
                        <SelectablePromotionRow
                            role="checkbox"
                            tone="credit"
                            checked={row.targetLevels.length === 0 || appliedLevel !== undefined}
                            disabled
                        >
                            {content}
                        </SelectablePromotionRow>
                    ) : (
                        content
                    ),
                };
            })}
            {...(applied && { limit: Number.POSITIVE_INFINITY })}
        />
    );
}
